import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense } from "react";
import { CityProvider } from "@/contexts/CityContext";
import Layout from "./components/Layout";
import Index from "./pages/Index";
import AdvertisingConsent from "./components/AdvertisingConsent";
const Privacidade = lazy(() => import('./pages/Privacidade'));

// Lazy load non-critical routes
const Diferencial = lazy(() => import("./pages/Diferencial"));
const QuemSomos = lazy(() => import("./pages/QuemSomos"));
const Contato = lazy(() => import("./pages/Contato"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Dedetizacao = lazy(() => import("./pages/servicos/Dedetizacao"));
const Desratizacao = lazy(() => import("./pages/servicos/Desratizacao"));
const LimpezaCaixaDagua = lazy(() => import("./pages/servicos/LimpezaCaixaDagua"));
const Descupinizacao = lazy(() => import("./pages/servicos/Descupinizacao"));
const Formigas = lazy(() => import("./pages/servicos/Formigas"));
const FOG = lazy(() => import("./pages/servicos/FOG"));
const Sanitizacao = lazy(() => import("./pages/servicos/Sanitizacao"));
const AntiTracas = lazy(() => import("./pages/servicos/AntiTracas"));
const CaixaGordura = lazy(() => import("./pages/servicos/CaixaGordura"));
const PulgasCarrapatos = lazy(() => import("./pages/servicos/PulgasCarrapatos"));
const AranhasEscorpioes = lazy(() => import("./pages/servicos/AranhasEscorpioes"));
const AfastamentoPombos = lazy(() => import("./pages/servicos/AfastamentoPombos"));
const BrasiliaHub = lazy(() => import("./pages/brasilia/BrasiliaHub"));
const BairroPage = lazy(() => import("./pages/brasilia/BairroPage"));
const BairroServicePage = lazy(() => import("./pages/brasilia/BairroServicePage"));
const RioHub = lazy(() => import("./pages/rio-de-janeiro/RioHub"));
const RioBairrosPage = lazy(() => import("./pages/rio-de-janeiro/RioBairrosPage"));
const RioBairroPage = lazy(() => import("./pages/rio-de-janeiro/RioBairroPage"));
const RioBairroServicePage = lazy(() => import("./pages/rio-de-janeiro/RioBairroServicePage"));
const GoianesiaHub = lazy(() => import("./pages/goianesia/GoianesiaHub"));
const GoiBairrosPage = lazy(() => import("./pages/goianesia/GoiBairrosPage"));
const GoiBairroPage = lazy(() => import("./pages/goianesia/GoiBairroPage"));
const GoiBairroServicePage = lazy(() => import("./pages/goianesia/GoiBairroServicePage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Beneficios = lazy(() => import("./pages/Beneficios"));
const Servicos = lazy(() => import("./pages/Servicos"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AssistenteIA = lazy(() => import("./pages/AssistenteIA"));
// Ads landing pages — eager import to eliminate Suspense gate before LCP
import AdsDedetizacaoUrgente from "./pages/ads/DedetizacaoUrgente";
import AdsDedetizacaoSegura from "./pages/ads/DedetizacaoSegura";


const queryClient = new QueryClient();

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

/** Shared service routes used for both Brasília (no prefix) and Rio (/rio-de-janeiro prefix) */
const serviceRoutes = (
  <>
    <Route path="servicos/dedetizacao" element={<Dedetizacao />} />
    <Route path="servicos/desratizacao" element={<Desratizacao />} />
    <Route path="servicos/limpeza-caixa-dagua" element={<LimpezaCaixaDagua />} />
    <Route path="servicos/descupinizacao" element={<Descupinizacao />} />
    <Route path="servicos/formigas" element={<Formigas />} />
    <Route path="servicos/fog" element={<FOG />} />
    <Route path="servicos/sanitizacao" element={<Sanitizacao />} />
    <Route path="servicos/anti-tracas" element={<AntiTracas />} />
    <Route path="servicos/caixa-gordura" element={<CaixaGordura />} />
    <Route path="servicos/pulgas-carrapatos" element={<PulgasCarrapatos />} />
    <Route path="servicos/aranhas-escorpioes" element={<AranhasEscorpioes />} />
    <Route path="servicos/afastamento-pombos" element={<AfastamentoPombos />} />
  </>
);

/** Shared institutional/blog routes */
const sharedRoutes = (
  <>
    <Route path="diferencial" element={<Diferencial />} />
    <Route path="o-diferencial-usa" element={<Diferencial />} />
    <Route path="quem-somos" element={<QuemSomos />} />
    <Route path="contato" element={<Contato />} />
    <Route path="beneficios" element={<Beneficios />} />
    <Route path="blog" element={<Blog />} />
    <Route path="dicas-de-dedetizacao" element={<Blog />} />
    <Route path="blog/:slug" element={<BlogPost />} />
    <Route path="servicos" element={<Servicos />} />
    <Route path="depoimentos" element={<Index />} />
  </>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AdvertisingConsent />
        <CityProvider>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/privacidade" element={<Privacidade />} />
              <Route element={<Layout />}>
                {/* Brasília (default - no prefix) */}
                <Route path="/" element={<Index />} />
                {sharedRoutes}
                {serviceRoutes}
                <Route path="/brasilia" element={<BrasiliaHub />} />
                <Route path="/brasilia/:bairro" element={<BairroPage />} />
                <Route path="/brasilia/:bairro/:servico" element={<BairroServicePage />} />

                {/* Rio de Janeiro (prefixed) */}
                <Route path="/rio-de-janeiro" element={<RioHub />} />
                <Route path="/rio-de-janeiro/bairros" element={<RioBairrosPage />} />
                <Route path="/rio-de-janeiro/:bairro" element={<RioBairroPage />} />
                <Route path="/rio-de-janeiro/:bairro/:servico" element={<RioBairroServicePage />} />
                <Route path="/rio-de-janeiro/servicos/dedetizacao" element={<Dedetizacao />} />
                <Route path="/rio-de-janeiro/servicos/desratizacao" element={<Desratizacao />} />
                <Route path="/rio-de-janeiro/servicos/limpeza-caixa-dagua" element={<LimpezaCaixaDagua />} />
                <Route path="/rio-de-janeiro/servicos/descupinizacao" element={<Descupinizacao />} />
                <Route path="/rio-de-janeiro/servicos/formigas" element={<Formigas />} />
                <Route path="/rio-de-janeiro/servicos/fog" element={<FOG />} />
                <Route path="/rio-de-janeiro/servicos/sanitizacao" element={<Sanitizacao />} />
                <Route path="/rio-de-janeiro/servicos/anti-tracas" element={<AntiTracas />} />
                <Route path="/rio-de-janeiro/servicos/caixa-gordura" element={<CaixaGordura />} />
                <Route path="/rio-de-janeiro/servicos/pulgas-carrapatos" element={<PulgasCarrapatos />} />
                <Route path="/rio-de-janeiro/servicos/aranhas-escorpioes" element={<AranhasEscorpioes />} />
                <Route path="/rio-de-janeiro/servicos/afastamento-pombos" element={<AfastamentoPombos />} />
                <Route path="/rio-de-janeiro/servicos" element={<Servicos />} />
                <Route path="/rio-de-janeiro/diferencial" element={<Diferencial />} />
                <Route path="/rio-de-janeiro/quem-somos" element={<QuemSomos />} />
                <Route path="/rio-de-janeiro/contato" element={<Contato />} />
                <Route path="/rio-de-janeiro/beneficios" element={<Beneficios />} />
                <Route path="/rio-de-janeiro/blog" element={<Blog />} />
                <Route path="/rio-de-janeiro/blog/:slug" element={<BlogPost />} />

                {/* Goianésia (prefixed) */}
                <Route path="/goianesia" element={<GoianesiaHub />} />
                <Route path="/goianesia/bairros" element={<GoiBairrosPage />} />
                <Route path="/goianesia/:bairro" element={<GoiBairroPage />} />
                <Route path="/goianesia/:bairro/:servico" element={<GoiBairroServicePage />} />
                <Route path="/goianesia/servicos/dedetizacao" element={<Dedetizacao />} />
                <Route path="/goianesia/servicos/desratizacao" element={<Desratizacao />} />
                <Route path="/goianesia/servicos/limpeza-caixa-dagua" element={<LimpezaCaixaDagua />} />
                <Route path="/goianesia/servicos/descupinizacao" element={<Descupinizacao />} />
                <Route path="/goianesia/servicos/formigas" element={<Formigas />} />
                <Route path="/goianesia/servicos/anti-tracas" element={<AntiTracas />} />
                <Route path="/goianesia/servicos/pulgas-carrapatos" element={<PulgasCarrapatos />} />
                <Route path="/goianesia/servicos/aranhas-escorpioes" element={<AranhasEscorpioes />} />
                <Route path="/goianesia/servicos" element={<Servicos />} />
                <Route path="/goianesia/diferencial" element={<Diferencial />} />
                <Route path="/goianesia/quem-somos" element={<QuemSomos />} />
                <Route path="/goianesia/contato" element={<Contato />} />
                <Route path="/goianesia/beneficios" element={<Beneficios />} />
                <Route path="/goianesia/blog" element={<Blog />} />
                <Route path="/goianesia/blog/:slug" element={<BlogPost />} />
              </Route>
              {/* Admin routes (no Layout) */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/assistente-ia" element={<AssistenteIA />} />
              {/* Google Ads landing pages - no Layout (no header/footer/chat/city popup) */}
              <Route path="/ads/dedetizacao-urgente/brasilia" element={<AdsDedetizacaoUrgente />} />
              <Route path="/ads/dedetizacao-segura/brasilia" element={<AdsDedetizacaoSegura />} />
              
              <Route element={<Layout />}>
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </Suspense>
        </CityProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
